import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, TrendingUp, AlertTriangle, CheckCircle2, Clock,
  ArrowUpRight, Calendar, DollarSign, Repeat
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Dashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('mstechs_token');
      const response = await fetch(`${API_URL}/api/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setDashboard(data.dashboard);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('sr-RS').format(Math.round(amount || 0)) + ' RSD';
  const formatDate = (dateString) =>
    dateString ? new Date(dateString).toLocaleDateString('sr-RS') : '-';

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Učitavanje...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* GORNJE STAT KARTICE */}
      <div className="stats-grid">
        <div className="stat-card stat-primary">
          <div className="stat-icon"><Users /></div>
          <div className="stat-content">
            <span className="stat-label">Ukupno klijenata</span>
            <span className="stat-value">{dashboard?.clients?.total || 0}</span>
            <span className="stat-detail">
              <span className="stat-active">{dashboard?.clients?.active || 0} aktivnih</span>
              {' · '}
              <span className="stat-inactive">{dashboard?.clients?.inactive || 0} neaktivnih</span>
            </span>
          </div>
        </div>

        <div className="stat-card stat-success">
          <div className="stat-icon"><TrendingUp /></div>
          <div className="stat-content">
            <span className="stat-label">Prosečan mesečni prihod</span>
            <span className="stat-value">{formatCurrency(dashboard?.income?.expectedAvgMonthly)}</span>
            <span className="stat-detail">
              <span className="stat-active">M: {formatCurrency(dashboard?.income?.expectedMonthly)}</span>
              {' · '}
              <span className="stat-inactive">G/12: {formatCurrency((dashboard?.income?.expectedYearly || 0) / 12)}</span>
            </span>
          </div>
        </div>

        <div className="stat-card stat-info">
          <div className="stat-icon"><DollarSign /></div>
          <div className="stat-content">
            <span className="stat-label">Prihod ovog meseca</span>
            <span className="stat-value">{formatCurrency(dashboard?.income?.currentMonth)}</span>
          </div>
        </div>

        <div className="stat-card stat-warning">
          <div className="stat-icon"><Clock /></div>
          <div className="stat-content">
            <span className="stat-label">Čeka naplatu</span>
            <span className="stat-value">{dashboard?.payments?.pending || 0}</span>
            <span className="stat-detail">
              <span className="stat-active">M: {dashboard?.payments?.pendingMonthly || 0}</span>
              {' · '}
              <span className="stat-inactive">G: {dashboard?.payments?.pendingYearly || 0}</span>
            </span>
          </div>
        </div>
      </div>

      {/* DONJE SEKCIJE */}
      <div className="dashboard-grid">
        {/* PREDSTOJECE NAPLATE */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2><Calendar size={20} /> Predstojeće naplate</h2>
            <Link to="/mstechs/clients" className="card-link">
              Pogledaj sve <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="card-content">
            {dashboard?.payments?.upcoming?.length > 0 ? (
              <div className="upcoming-list">
                {dashboard.payments.upcoming.slice(0, 6).map((item) => (
                  <div key={`${item._id}-${item.type}`} className="upcoming-item">
                    <div className="upcoming-info">
                      <span className="upcoming-name">
                        {item.name}
                        <span className={`type-badge ${item.type}`}>
                          {item.type === 'monthly' ? <><Repeat size={11} /> M</> : <><Calendar size={11} /> G</>}
                        </span>
                      </span>
                      <span className="upcoming-date">{formatDate(item.dueDate)}</span>
                    </div>
                    <span className="upcoming-amount">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <CheckCircle2 size={32} />
                <p>Nema predstojećih naplata</p>
              </div>
            )}
          </div>
        </div>

        {/* ZAKASNELE NAPLATE */}
        <div className="dashboard-card">
          <div className="card-header">
            <h2><AlertTriangle size={20} /> Zakasnele naplate</h2>
          </div>
          <div className="card-content">
            {dashboard?.payments?.overdue?.length > 0 ? (
              <div className="overdue-list">
                {dashboard.payments.overdue.map((item) => (
                  <div key={`${item._id}-${item.type}`} className="overdue-item">
                    <div className="overdue-info">
                      <span className="overdue-name">
                        {item.name}
                        <span className={`type-badge ${item.type}`}>
                          {item.type === 'monthly' ? <><Repeat size={11} /> M</> : <><Calendar size={11} /> G</>}
                        </span>
                      </span>
                      <span className="overdue-date">Rok: {formatDate(item.dueDate)}</span>
                    </div>
                    <span className="overdue-amount">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state success">
                <CheckCircle2 size={32} />
                <p>Sve naplate su na vreme!</p>
              </div>
            )}
          </div>
        </div>

        {/* POSLEDNJE UPLATE */}
        <div className="dashboard-card full-width">
          <div className="card-header">
            <h2><TrendingUp size={20} /> Poslednje uplate</h2>
            <Link to="/mstechs/income" className="card-link">
              Svi prihodi <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="card-content">
            {dashboard?.recentPayments?.length > 0 ? (
              <div className="recent-payments-table">
                <table>
                  <thead>
                    <tr>
                      <th>Klijent</th>
                      <th>Tip</th>
                      <th>Datum</th>
                      <th>Iznos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.recentPayments.map((payment) => (
                      <tr key={payment._id}>
                        <td>{payment.clientName}</td>
                        <td>
                          <span className={`type-badge ${payment.type}`}>
                            {payment.type === 'monthly' ? <><Repeat size={11} /> Mesečna</> : <><Calendar size={11} /> Godišnja</>}
                          </span>
                        </td>
                        <td>{formatDate(payment.paymentDate)}</td>
                        <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <DollarSign size={32} />
                <p>Nema evidentiranih uplata</p>
              </div>
            )}
          </div>
        </div>

        {/* SUMARY */}
        <div className="dashboard-card summary-card">
          <div className="summary-content">
            <div className="summary-icon"><TrendingUp size={32} /></div>
            <div className="summary-text">
              <span className="summary-label">Ukupan prihod (svo vreme)</span>
              <span className="summary-value">{formatCurrency(dashboard?.income?.totalAllTime)}</span>
              <span className="summary-detail">
                {dashboard?.income?.totalPayments || 0} evidentiranih uplata
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;